import React from 'react';
import { Stack, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import { Event, EventStatus } from '@/types/events';
import { EventCard } from '../EventCard';

interface EventColumnProps {
  events: Event[];
  selectedStatus: EventStatus | null;
  onStatusChange: (status: EventStatus | null) => void;
  onEdit: (event: Event) => void;
  onDelete: (id: string) => void;
  onView: (event: Event) => void;
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

export const EventColumn: React.FC<EventColumnProps> = ({
  events,
  selectedStatus,
  onStatusChange,
  onEdit,
  onDelete,
  onView
}) => {
  return (
    <Stack 
      spacing={2} 
      sx={{ 
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <FormControl size="small">
        <InputLabel>Filter by Status</InputLabel>
        <Select
          value={selectedStatus || ''}
          label="Filter by Status"
          onChange={(e) => onStatusChange(e.target.value as EventStatus | null)}
          sx={{ bgcolor: 'background.paper' }}
        >
          <MenuItem value="">All Events</MenuItem>
          {STATUS_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{
        flexGrow: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        pr: 1,
        '&::-webkit-scrollbar': {
          width: 8
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.400',
          borderRadius: 1,
          '&:hover': {
            bgcolor: 'grey.500'
          }
        }
      }}>
        {events.map(event => (
          <EventCard
            key={event.id}
            event={event}
            onEdit={onEdit}
            onDelete={onDelete}
            onView={onView}
          />
        ))}
        {events.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            No events found
          </Box>
        )}
      </Box>
    </Stack>
  );
};